<template>
  <v-card>
    <!-- Bulk Actions Toolbar -->
    <v-toolbar v-if="!requestMode && selectedItemsModel.length > 0" color="primary" dark density="compact">
      <v-toolbar-title>{{ selectedItemsModel.length }} item(s) selected</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn
        v-if="isAdmin"
        variant="text"
        @click="$emit('bulkEdit', selectedItemsModel)"
        prepend-icon="mdi-pencil"
      >
        Bulk Edit
      </v-btn>
      <v-btn
        variant="text"
        @click="$emit('printPackingSlips', selectedItemsModel)"
        prepend-icon="mdi-printer"
      >
        Print Packing Slips
      </v-btn>
      <v-btn
        variant="text"
        @click="$emit('printDonationReceipts', selectedItemsModel)"
        prepend-icon="mdi-receipt-text-outline"
      >
        Print Receipts
      </v-btn>
    </v-toolbar>

    <v-data-table
      :headers="headers"
      :items="equipment"
      :loading="loading"
      :items-per-page="itemsPerPage"
      :show-select="!requestMode"
      v-model="selectedItemsModel"
      v-model:expanded="expandedItems"
      item-value="id"
      class="elevation-1 clickable-table"
      @click:row="toggleRowExpansion"
    >
      <!-- Equipment Type Column -->
      <template v-slot:item.equipment_type="{ item }">
        <v-chip color="secondary" size="small">
          {{ (item as any).equipment_type }}
        </v-chip>
      </template>

      <!-- Rating Column -->
      <template v-slot:item.rating="{ item }">
        <v-chip
          :color="getRatingColor((item as any).rating)"
          size="small"
          variant="flat"
          :style="{ opacity: 0.85 }"
        >
          {{ (item as any).rating || '-' }}
        </v-chip>
      </template>

      <!-- Brand/Model Column -->
      <template v-slot:item.manufacturer="{ item }">
        <div>
          <strong>{{ (item as any).manufacturer || '-' }}</strong>
          {{ (item as any).model ? ` ${(item as any).model}` : '' }}
        </div>
      </template>

      <!-- Donor Column -->
      <template v-slot:item.donor="{ item }">
        <span v-if="(item as any).donor">{{ (item as any).donor }}</span>
        <span v-else class="text-grey">-</span>
      </template>

      <!-- Status Column -->
      <template v-slot:item.status="{ item }">
        <v-chip
          :color="getStatusColor((item as any).status)"
          size="small"
          variant="tonal"
        >
          {{ formatStatus((item as any).status) }}
        </v-chip>
      </template>

      <!-- Claimed Status Column -->
      <template v-slot:item.claimed="{ item }">
        <div v-if="(item as any).claimed_by">
          <v-chip color="warning" size="small" variant="tonal">
            <v-icon start size="small">mdi-account-check</v-icon>
            Claimed
          </v-chip>
          <div class="text-caption text-grey mt-1">
            For: {{ (item as any).claimed_for }}
          </div>
        </div>
        <v-chip v-else color="success" size="small" variant="tonal">
          <v-icon start size="small">mdi-check-circle</v-icon>
          Available
        </v-chip>
      </template>

      <!-- Actions Column -->
      <template v-slot:item.actions="{ item }">
        <div v-if="requestMode" class="d-flex ga-2" @click.stop>
          <v-btn
            v-if="!(item as any).claimed_by"
            color="success"
            size="small"
            @click="$emit('claim', item)"
          >
            <v-icon start>mdi-hand-extended</v-icon>
            Claim
          </v-btn>
          <v-btn
            v-else
            color="warning"
            size="small"
            @click="$emit('unclaim', item)"
          >
            <v-icon start>mdi-cancel</v-icon>
            Unclaim
          </v-btn>
        </div>
        <div v-else class="d-flex ga-2" @click.stop>
          <v-btn
            icon="mdi-receipt-text-outline"
            size="small"
            variant="text"
            color="primary"
            title="Print donation receipt"
            @click="$emit('printDonationReceipts', [(item as any).id])"
          ></v-btn>
          <v-btn
            v-if="!(item as any).claimed_by && (item as any).status === 'available'"
            color="info"
            size="small"
            variant="text"
            @click="$emit('claim', item)"
            title="Claim by name"
          >
            <v-icon>mdi-hand-extended</v-icon>
          </v-btn>
          <v-btn
            v-if="isAdmin"
            icon="mdi-pencil"
            size="small"
            variant="text"
            @click="$emit('edit', item)"
          ></v-btn>
          <v-btn
            v-if="isAdmin"
            icon="mdi-delete"
            size="small"
            variant="text"
            color="error"
            @click="$emit('delete', item)"
          ></v-btn>
        </div>
      </template>

      <!-- Expandable Row Details -->
      <template v-slot:expanded-row="{ item }">
        <tr>
          <td :colspan="headers.length + 1">
            <v-card flat class="ma-4">
              <v-card-title class="text-h6">Equipment Details</v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="12" sm="6" md="4">
                    <div class="text-caption text-grey">CPU</div>
                    <div>{{ (item as any).cpu || 'N/A' }}</div>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <div class="text-caption text-grey">RAM</div>
                    <div>{{ (item as any).ram || 'N/A' }}</div>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <div class="text-caption text-grey">Storage</div>
                    <div>{{ (item as any).storage || 'N/A' }}</div>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <div class="text-caption text-grey">GPU</div>
                    <div>{{ (item as any).gpu || 'N/A' }}</div>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <div class="text-caption text-grey">OS</div>
                    <div>{{ (item as any).os || 'N/A' }}</div>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <div class="text-caption text-grey">Screen</div>
                    <div>{{ (item as any).screen || 'N/A' }}</div>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <div class="text-caption text-grey">Value</div>
                    <div>{{ (item as any).value != null ? formatCurrency((item as any).value) : 'N/A' }}</div>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <div class="text-caption text-grey">Donor</div>
                    <div>{{ (item as any).donor || 'N/A' }}</div>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <div class="text-caption text-grey">Donated On</div>
                    <div>{{ formatDate((item as any).donated_on) }}</div>
                  </v-col>
                  <v-col v-if="(item as any).comments" cols="12">
                    <div class="text-caption text-grey">Comments</div>
                    <div>{{ (item as any).comments }}</div>
                  </v-col>

                  <!-- Task Compatibility (Request Mode Only) -->
                  <v-col v-if="requestMode && requestData && requestData.needed_for" cols="12">
                    <div class="text-caption text-grey mb-2">Task Compatibility</div>
                    <div class="d-flex flex-wrap ga-2">
                      <v-chip
                        v-for="task in requestData.needed_for"
                        :key="task"
                        size="small"
                        :color="isTaskSupported(item, task) ? 'success' : 'error'"
                        :variant="isTaskSupported(item, task) ? 'flat' : 'tonal'"
                      >
                        <v-icon v-if="isTaskSupported(item, task)" start size="small">mdi-check</v-icon>
                        <v-icon v-else start size="small">mdi-close</v-icon>
                        {{ formatTaskName(task) }}
                      </v-chip>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
              <v-card-actions v-if="!requestMode">
                <v-spacer></v-spacer>
                <v-btn
                  color="primary"
                  variant="text"
                  @click="$emit('printDonationReceipts', [(item as any).id])"
                >
                  <v-icon start>mdi-receipt-text-outline</v-icon>
                  Print Donation Receipt
                </v-btn>
                <v-btn
                  v-if="isAdmin"
                  color="primary"
                  variant="outlined"
                  @click="$emit('addMoreOfModel', item)"
                >
                  <v-icon start>mdi-plus-circle</v-icon>
                  Add More of This Model
                </v-btn>
              </v-card-actions>
            </v-card>
          </td>
        </tr>
      </template>

      <!-- Loading Slot -->
      <template v-slot:loading>
        <v-skeleton-loader type="table-row@10"></v-skeleton-loader>
      </template>

      <!-- No Data Slot -->
      <template v-slot:no-data>
        <v-alert type="info" variant="tonal" class="ma-4">
          No equipment found
        </v-alert>
      </template>
    </v-data-table>
  </v-card>
</template>

<script setup lang="ts">
/* eslint-disable no-undef */
import { ref, computed } from 'vue'

interface Equipment {
  id: string
  equipment_type: string
  rating: number | null
  manufacturer: string
  model: string
  status: string
  location: string
  cpu?: string
  ram?: string
  storage?: string
  gpu?: string
  os?: string
  screen?: string
  estimated_value?: number
  value?: number | null
  donor?: string
  donated_on?: string | null
  comments?: string
  claimed_by?: string
  claimed_for?: string
}

interface Props {
  equipment: Equipment[]
  selectedItems?: string[]
  loading?: boolean
  isAdmin?: boolean
  requestMode?: boolean
  itemsPerPage?: number
  requestData?: any  // The request object with needed_for tasks
}

const props = withDefaults(defineProps<Props>(), {
  selectedItems: () => [],
  loading: false,
  isAdmin: false,
  requestMode: false,
  itemsPerPage: 25,
  requestData: null
})

const emit = defineEmits(['update:selectedItems', 'edit', 'delete', 'claim', 'unclaim', 'bulkEdit', 'printPackingSlips', 'printDonationReceipts', 'addMoreOfModel'])

const expandedItems = ref<any[]>([])

const selectedItemsModel = computed({
  get: () => props.selectedItems,
  set: (value) => emit('update:selectedItems', value)
})

const toggleRowExpansion = (event: any, { item }: any) => {
  // VDataTable passes the raw item
  const itemId = item.id
  const index = expandedItems.value.findIndex((id: any) => id === itemId)

  if (index > -1) {
    // Item is expanded, collapse it
    expandedItems.value.splice(index, 1)
  } else {
    // Item is collapsed, expand it
    expandedItems.value.push(itemId)
  }
}

const headers = computed(() => {
  const baseHeaders = [
    { title: 'Asset Tag', key: 'asset_tag', sortable: true },
    { title: 'Type', key: 'equipment_type', sortable: true },
    { title: 'Rating', key: 'rating', sortable: true },
    { title: 'Brand/Model', key: 'manufacturer', sortable: true },
    { title: 'Status', key: 'status', sortable: true },
    { title: 'Location', key: 'location', sortable: true }
  ]

  if (!props.requestMode) {
    baseHeaders.push({ title: 'Donor', key: 'donor', sortable: true })
  }

  // if (props.requestMode) {
  //   baseHeaders.push({ title: 'Claimed', key: 'claimed', sortable: true })
  // }

  if (props.requestMode || props.isAdmin) {
    baseHeaders.push({ title: 'Actions', key: 'actions', sortable: false })
  }

  return baseHeaders
})

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount)
}

const formatDate = (value?: string | null) => {
  if (!value) return 'N/A'
  const date = new Date(value)
  if (isNaN(date.getTime())) return 'N/A'
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

const formatStatus = (status: string) => {
  if (!status) return 'Unknown'
  return status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')
}

const getRatingColor = (rating: number | null) => {
  if (!rating) return 'grey'
  // Yellow to green gradient: lower ratings are more yellow, higher are more green
  if (rating >= 8) return 'success' // Green
  if (rating >= 6) return 'light-green' // Light green
  if (rating >= 4) return 'lime' // Lime (yellow-green)
  return 'yellow-darken-2' // Yellow
}

const getStatusColor = (status: string) => {
  const statusColors: Record<string, string> = {
    processing: 'info',
    troubleshooting: 'warning',
    parts: 'deep-orange-darken-2',
    available: 'success',
    in_inventory: 'success',
    delivered: 'info',
    maintenance: 'warning',
    retired: 'error',
    claimed: 'warning'
  }
  return statusColors[status] || 'grey'
}

// Task capability checking based on equipment rating
const getMinimumRatingForTask = (task: string): number => {
  const taskRequirements: Record<string, number> = {
    email: 1,
    web_browsing: 1,
    office_work: 1,
    document_editing: 1,
    video_conferencing: 4,
    presentations: 4,
    coding: 4,
    photo_editing: 6,
    music_production: 6,
    video_editing: 8,
    '3d_modeling': 8,
    gaming: 8,
    creative_work: 8,
    animation: 8,
    machine_learning: 8,
    cad: 8
  }
  return taskRequirements[task] || 1
}

const isTaskSupported = (equipment: any, task: string): boolean => {
  const rating = equipment.rating || 0
  const minRating = getMinimumRatingForTask(task)
  return rating >= minRating
}

const formatTaskName = (task: string): string => {
  const taskLabels: Record<string, string> = {
    email: 'Email',
    web_browsing: 'Web Browsing',
    office_work: 'Office Work',
    document_editing: 'Document Editing',
    video_conferencing: 'Video Conferencing',
    presentations: 'Presentations',
    coding: 'Programming/Coding',
    photo_editing: 'Photo Editing',
    video_editing: 'Video Editing',
    '3d_modeling': '3D Modeling',
    gaming: 'Gaming',
    creative_work: 'Creative Work',
    machine_learning: 'Machine Learning',
    cad: 'CAD',
    music_production: 'Music Production',
    animation: 'Animation'
  }
  return taskLabels[task] || task.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
}
</script>

<style scoped>
.clickable-table :deep(tbody tr) {
  cursor: pointer;
}

.clickable-table :deep(tbody tr:hover) {
  background-color: rgba(0, 0, 0, 0.04);
}
</style>
